/**
 * Composable pour le formatage des champs de formulaire
 */

/**
 * Toutes les durées écrites dans un libellé du catalogue, en heures.
 * « 2H00 ou 3H30 » → [2, 3.5] ; « 5 jours (35 heures…) » → [35] ; « 7h » → [7].
 * @param {string} duree
 * @returns {number[]}
 */
export function parseFormationDurations(duree) {
  if (!duree || typeof duree !== 'string') return []
  const values = []
  for (const match of duree.matchAll(/(\d+(?:[.,]\d+)?)\s*(?:h(\d{2})|heures?|h)/gi)) {
    const hours = parseFloat(match[1].replace(',', '.'))
    const minutes = match[2] ? parseInt(match[2], 10) : 0
    if (Number.isFinite(hours) && minutes < 60) values.push(hours + minutes / 60)
  }
  return values
}

export function useFormFormatting() {
  /**
   * Formater un SIRET (ne garder que les chiffres, limiter à 14)
   * @param {string} value - Valeur à formater
   * @returns {string} SIRET formaté
   */
  const formatSiret = (value) => {
    if (!value) return ''
    // Ne garder que les chiffres
    let formatted = value.replace(/\D/g, '')
    // Limiter à 14 caractères
    return formatted.substring(0, 14)
  }

  /**
   * Formater un code postal (ne garder que les chiffres, limiter à 5)
   * @param {string} value - Valeur à formater
   * @returns {string} Code postal formaté
   */
  const formatCodePostal = (value) => {
    if (!value) return ''
    // Ne garder que les chiffres
    let formatted = value.replace(/\D/g, '')
    // Limiter à 5 caractères
    return formatted.substring(0, 5)
  }

  /**
   * Calculer le numéro de TVA à partir du SIREN
   * @param {string} siren - SIREN (9 chiffres)
   * @returns {string|null} Numéro de TVA formaté (FR + clé + SIREN) ou null si invalide
   */
  const calculateNumeroTva = (siren) => {
    if (!siren || siren.length < 9) {
      return null
    }
    
    // Extraire le SIREN (9 premiers chiffres)
    const sirenStr = siren.toString().replace(/\D/g, '').substring(0, 9)
    
    if (sirenStr.length !== 9) {
      return null
    }
    
    // Calculer la clé : (12 + 3 × (SIREN mod 97)) mod 97
    const sirenNum = parseInt(sirenStr, 10)
    const cle = (12 + 3 * (sirenNum % 97)) % 97
    
    // Formater la clé sur 2 chiffres avec un zéro devant si nécessaire
    const cleFormatted = cle.toString().padStart(2, '0')
    
    // Retourner FR + clé + SIREN
    return `FR${cleFormatted}${sirenStr}`
  }

  /**
   * Formater un numéro de téléphone (français ou international)
   * @param {string} value - Valeur à formater
   * @returns {string} Téléphone formaté (ex: 01 23 45 67 89 ou +41 79 123 45 67)
   */
  const formatTelephone = (value) => {
    if (!value) return ''

    const hasPlus = value.trim().startsWith('+')

    // Conserver uniquement les chiffres
    let cleaned = value.replace(/\D/g, '')

    // Permettre la saisie du "+" seul (l'utilisateur est en train de taper)
    if (hasPlus && cleaned.length === 0) {
      return '+'
    }

    // +33 → convertir en format français seulement si le numéro est complet (11 chiffres: 33 + 9)
    if (cleaned.startsWith('33') && hasPlus && cleaned.length >= 11) {
      cleaned = '0' + cleaned.slice(2)
    }

    // Numéro français (commence par 0) : limiter à 10 chiffres
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(0, 10)
      const formatted = cleaned.match(/.{1,2}/g)?.join(' ') || cleaned
      return formatted
    }

    // Numéro international : conserver le + et limiter à 15 chiffres (norme E.164)
    if (hasPlus && cleaned.length > 0) {
      cleaned = cleaned.substring(0, 15)
      return '+' + cleaned
    }

    // Fallback : groupes de 2 chiffres
    if (cleaned.length > 0) {
      cleaned = cleaned.substring(0, 15)
      const formatted = cleaned.match(/.{1,2}/g)?.join(' ') || cleaned
      return formatted
    }

    return ''
  }

  /**
   * Parser la durée d'une formation (ex: "7h", "14H00", "3h30", "0H30",
   * "7 heures", "3.5h"). Les minutes après le « h » comptent : « 3h30 » vaut
   * 3,5 et non 3, « 0H30 » vaut 0,5 et non 0. Quand le catalogue propose
   * plusieurs durées (« 2H00 ou 3H30 »), la plus longue l'emporte : c'est elle
   * qui fixe le nombre de jours à prévoir.
   * @param {string} duree - Durée au format texte
   * @returns {number} Durée en heures (0 si invalide)
   */
  const parseFormationDuration = (duree) => {
    const values = parseFormationDurations(duree)
    return values.length ? Math.max(...values) : 0
  }

  return {
    formatSiret,
    formatCodePostal,
    formatTelephone,
    calculateNumeroTva,
    parseFormationDuration,
    parseFormationDurations
  }
}

